import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionHeading from "@/components/ui/SectionHeading";

gsap.registerPlugin(ScrollTrigger);

interface EditorialBlockProps {
  scriptAccent?: string;
  heading: string;
  body: string;
  imageSrc?: string;
  imageAlt?: string;
  imagePosition?: "left" | "right";
  className?: string;
}

export default function EditorialBlock({
  scriptAccent,
  heading,
  body,
  imageSrc,
  imageAlt = "",
  imagePosition = "right",
  className = "",
}: EditorialBlockProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const elements = el.querySelectorAll(".editorial-animate");
    gsap.fromTo(
      elements,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      },
    );

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === el) st.kill();
      });
    };
  }, []);

  const hasImage = !!imageSrc;

  return (
    <section
      ref={ref}
      className={`px-6 py-24 lg:px-12 lg:py-40 ${className}`}
    >
      {hasImage ? (
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-2 lg:items-center lg:gap-24">
          {/* Image (left) */}
          {imagePosition === "left" && (
            <div className="editorial-animate overflow-hidden rounded-sm shadow-lg ring-1 ring-warm-stone/10">
              <img
                src={imageSrc}
                alt={imageAlt}
                className="editorial-image h-auto w-full object-cover"
                loading="lazy"
              />
            </div>
          )}

          {/* Text */}
          <div>
            <div className="editorial-animate">
              <SectionHeading
                scriptAccent={scriptAccent}
                heading={heading}
                centered={false}
              />
            </div>
            <p className="editorial-animate max-w-prose font-body text-lg leading-[1.9] text-soft-charcoal/80 lg:text-xl">
              {body}
            </p>
          </div>

          {/* Image (right) */}
          {imagePosition === "right" && (
            <div className="editorial-animate order-first overflow-hidden rounded-sm shadow-lg ring-1 ring-warm-stone/10 lg:order-none">
              <img
                src={imageSrc}
                alt={imageAlt}
                className="editorial-image h-auto w-full object-cover"
                loading="lazy"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="mx-auto max-w-3xl text-center">
          <div className="editorial-animate">
            <SectionHeading
              scriptAccent={scriptAccent}
              heading={heading}
              centered
            />
          </div>
          <p className="editorial-animate mx-auto max-w-2xl font-body text-lg leading-[1.9] text-soft-charcoal/80 lg:text-xl">
            {body}
          </p>
        </div>
      )}
    </section>
  );
}
